import * as CONSTANTS from "../utils/constants/constants";
import axios from "./axiosInstance";

export interface StoredTranscript {
  transcript_id: string;
  intervention_id: string;
  filename: string;
  content_type: string;
  timestamp?: string;
  datetime?: string;
}

export interface TranscriptUtterance {
  start: number;
  end: number;
  text: string;
  speaker?: string;
}

export interface TranscriptDetail extends StoredTranscript {
  text?: string;
  summary?: string;
  utterances: TranscriptUtterance[];
  startedAt?: number;
  durationMs?: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isMetadata(value: unknown, interventionId: string): value is StoredTranscript {
  return isObject(value) &&
    typeof value.transcript_id === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.transcript_id) &&
    value.intervention_id === interventionId &&
    typeof value.filename === "string" &&
    typeof value.content_type === "string" &&
    (value.timestamp === undefined || typeof value.timestamp === "string") &&
    (value.datetime === undefined || typeof value.datetime === "string");
}

function endpoint(deviceId: string): string {
  return `${CONSTANTS.HOST}api/v1/device/${encodeURIComponent(deviceId)}/transcripts/`;
}

export async function listDeviceTranscripts(
  deviceId: string,
  interventionId: string,
  accessToken: string,
  signal: AbortSignal,
  offset = 0,
  limit = 50,
): Promise<StoredTranscript[]> {
  const response = await axios.get<unknown>(endpoint(deviceId), {
    params: { intervention_id: interventionId, limit, offset },
    headers: { authorization: `Bearer ${accessToken}` },
    signal,
  });
  const data = response.data;
  if (!isObject(data) || !Array.isArray(data.results) ||
      !data.results.every((item): item is StoredTranscript => isMetadata(item, interventionId))) {
    throw new Error("The backend returned an invalid transcript list");
  }
  return data.results;
}

export async function getDeviceTranscript(
  deviceId: string,
  interventionId: string,
  transcriptId: string,
  accessToken: string,
  signal: AbortSignal,
): Promise<TranscriptDetail> {
  const response = await axios.get<unknown>(
    `${endpoint(deviceId)}${encodeURIComponent(transcriptId)}/`,
    {
      params: { intervention_id: interventionId },
      headers: { authorization: `Bearer ${accessToken}` },
      signal,
    },
  );
  const packet = response.data;
  if (!isMetadata(packet, interventionId) || packet.transcript_id !== transcriptId ||
      !isObject(packet) || typeof packet.content !== "string") {
    throw new Error("The backend returned an invalid transcript");
  }
  if (packet.content_type !== "application/json") {
    return { ...packet, text: packet.content, utterances: [] };
  }
  const data: unknown = JSON.parse(packet.content);
  if (!isObject(data)) {
    return { ...packet, text: JSON.stringify(data, null, 2), utterances: [] };
  }
  const utterances: TranscriptUtterance[] = [];
  if (data.utterances !== undefined) {
    if (!Array.isArray(data.utterances)) throw new Error("Invalid transcript utterances");
    for (const item of data.utterances) {
      if (!isObject(item) || typeof item.text !== "string" ||
          typeof item.start !== "number" || !Number.isFinite(item.start) || item.start < 0 ||
          typeof item.end !== "number" || !Number.isFinite(item.end) || item.end < item.start ||
          (item.speaker !== undefined && typeof item.speaker !== "string")) {
        throw new Error("Invalid transcript utterance");
      }
      utterances.push({ start: item.start, end: item.end, text: item.text, speaker: item.speaker });
    }
    utterances.sort((a, b) => a.start - b.start);
  }
  const session = isObject(data.session) ? data.session : null;
  const startedAt = typeof session?.startedAt === "number" && Number.isFinite(session.startedAt)
    ? session.startedAt : undefined;
  const endedAt = typeof session?.endedAt === "number" && Number.isFinite(session.endedAt)
    ? session.endedAt : undefined;
  return {
    ...packet,
    utterances,
    text: typeof data.transcript === "string" ? data.transcript
      : typeof data.text === "string" ? data.text
      : data.utterances === undefined ? JSON.stringify(data, null, 2) : undefined,
    summary: typeof data.summary === "string" ? data.summary : undefined,
    startedAt,
    durationMs: startedAt !== undefined && endedAt !== undefined && endedAt >= startedAt
      ? endedAt - startedAt : undefined,
  };
}
